/** Marketplace home — open to everyone, no sign-in. */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getPublicConfig, listApps, money, mktError, type AppCard, type PublicConfig } from '@/api/marketplace'
import MarketplaceShell, { AppTile, Check } from './MarketplaceShell'

export default function MarketplaceHome() {
  const [apps, setApps] = useState<AppCard[] | null>(null)
  const [config, setConfig] = useState<PublicConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('All')

  useEffect(() => {
    getPublicConfig().then(setConfig).catch(() => setConfig(null))
    listApps()
      .then((r) => setApps(r.apps))
      .catch((e) => setError(mktError(e, "We couldn't load the marketplace. Please refresh.").message))
  }, [])

  const categories = useMemo(() => {
    const present = new Set((apps || []).map((a) => a.category))
    return ['All', ...(config?.categories || []).filter((c) => present.has(c))]
  }, [apps, config])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (apps || []).filter((a) => (cat === 'All' || a.category === cat)
      && (!needle || `${a.name} ${a.tagline} ${a.category} ${a.publisher}`.toLowerCase().includes(needle)))
  }, [apps, q, cat])

  return (
    <MarketplaceShell>
      <section className="mkt-hero">
        <div className="mkt-hero-in">
          <span className="mkt-kicker">Ready-made apps for your business</span>
          <h1>Find the app your team needs. Start using it today.</h1>
          <p>Pick an app, choose how many people will use it, and pay monthly. We host it, keep it running and keep it updated — no IT team needed.</p>
          <form className="mkt-search" role="search" onSubmit={(e) => e.preventDefault()}>
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <label htmlFor="mkt-q" className="mkt-sr">Search apps</label>
            <input id="mkt-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try “inventory”, “clinic” or “invoices”" />
            <button type="submit" className="btn btn-primary">Search</button>
          </form>
          <div className="mkt-ticks">
            <span><Check />Free trial on many apps</span>
            <span><Check />Cancel any time</span>
            <span><Check />One clear monthly price</span>
          </div>
        </div>
      </section>

      <div className="mkt-wrap" style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {categories.length > 1 && (
          <div className="mkt-chips" role="group" aria-label="Categories">
            {categories.map((c) => (
              <button key={c} type="button" className={`mkt-chip${cat === c ? ' on' : ''}`} aria-pressed={cat === c} onClick={() => setCat(c)}>{c}</button>
            ))}
          </div>
        )}
        <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ fontSize: 24 }}>{cat === 'All' ? 'Apps' : `${cat} apps`}</h2>
          {apps && <span className="mkt-muted">{shown.length} {shown.length === 1 ? 'app' : 'apps'}</span>}
        </div>

        {error && <div className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div></div>}
        {!apps && !error && <div className="mkt-muted">Loading apps…</div>}
        {apps && shown.length === 0 && (
          <div className="mkt-empty">
            <strong style={{ fontSize: 18 }}>{apps.length === 0 ? 'The marketplace is just opening' : `No apps match “${q}”`}</strong>
            <span className="mkt-muted">{apps.length === 0 ? 'New apps appear here as soon as they pass review.' : 'Try a simpler word, or browse all apps.'}</span>
            {apps.length > 0 && <button type="button" className="btn btn-secondary" onClick={() => { setQ(''); setCat('All') }}>Show all apps</button>}
          </div>
        )}
        <div className="mkt-grid">
          {shown.map((a) => (
            <Link key={a.slug} to={`/marketplace/app/${a.slug}`} className="mkt-card">
              <div className="mkt-row" style={{ gap: 14, flexWrap: 'nowrap' }}>
                <AppTile color={a.icon_color} text={a.initials} size={52} />
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <strong style={{ fontSize: 18 }}>{a.name}</strong>
                  <span className="mkt-muted" style={{ fontSize: 14 }}>by {a.publisher}</span>
                </div>
              </div>
              <p>{a.tagline}</p>
              <div className="mkt-row" style={{ gap: 8 }}>
                <span className="mkt-pill">{a.category}</span>
                {a.trial_days > 0 && <span className="mkt-pill ok">Free {a.trial_days}-day trial</span>}
              </div>
              <div className="mkt-card-foot">
                <span>from <strong>{money(a.from_price_minor, a.currency)}</strong> {a.from_basis === 'person' ? '/ person / month' : '/ month'}</span>
              </div>
            </Link>
          ))}
        </div>

        <section aria-labelledby="how" style={{ marginTop: 24 }}>
          <h2 id="how" style={{ fontSize: 24, marginBottom: 16 }}>How it works</h2>
          <div className="mkt-grid">
            {[
              ['1', 'Find an app', "Browse freely — no account needed. Every app shows its full price and exactly what's included."],
              ['2', 'Choose your plan', 'Pay for each person who uses it, or for a whole server your team shares. Change it whenever you like.'],
              ['3', 'Start working in minutes', "We set everything up for you in your organization's own private database. Invite your team and you're ready."],
            ].map(([n, t, d]) => (
              <div key={n} className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="mkt-tile" style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--color-accent-weak)', color: 'var(--color-accent)' }}>{n}</span>
                <strong style={{ fontSize: 17 }}>{t}</strong>
                <span className="mkt-muted">{d}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mkt-dark mkt-row" style={{ justifyContent: 'space-between', gap: 20, marginTop: 8 }}>
          <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ fontSize: 22 }}>Built an app on Twasta? Sell it here.</strong>
            <span className="mkt-muted">Set your price, and we handle hosting, payments and billing. You earn every month from every customer. Available on Twasta Pro and above.</span>
          </div>
          <Link to="/marketplace/sell" className="btn btn-lg" style={{ background: '#fff', color: '#15181E' }}>Publish your app</Link>
        </section>
      </div>
    </MarketplaceShell>
  )
}
